(ns shared.core.components.recharts.donut-chart
  (:require [shared.core.style :as style]
            [shared.core.reactive :as re]
            [shared.core.components.recharts.util :as recharts.util]
            [shared.core.components.recharts.legend :as legend]
            [shared.core.components.recharts.label :as label]
            [shared.core.components.recharts.tooltip :as tooltip]
            [shared.core.util :as util]))

(style/add-css [".recharts-pie-sector" {:cursor "pointer"}])

(defn custom-stack-tooltip [{:keys [hovered-item* mouse-position* chart-bounds* show-double-click-message?]}]
  (when @hovered-item*
    (let [{:keys [x y]} @mouse-position*
          chart-bounds @chart-bounds*
          chart-center-x (when chart-bounds
                           (+ (.-left chart-bounds) (/ (.-width chart-bounds) 2)))
          should-flip? (and chart-center-x (> x chart-center-x))]
      [tooltip/tooltip-panel {:style-map (tooltip/floating-style x y {:tooltip-width 320})
                              :show-double-click-message? show-double-click-message?
                              :skip-translate-x? should-flip?}])))

(defn donut-chart
  "Renders a donut (single-ring pie) chart with legend, laid out by
   `legend/chart-with-legend`: `:width`/`:height` are the LARGEST the donut will draw
   and it shrinks to fit a narrower container.

   Parameters:
   - :data - Vector of maps with :name, :value, and :fill keys
   - :data-keys-config - Slice colors keyed by slice name, each {:fill \"#hex\"} (default: from the name)
   - :width - Max chart width (default: 500)
   - :height - Max chart height (default: 500)
   - :inner-radius - Inner radius % (default: 50)
   - :outer-radius - Outer radius % (default: 80)
   - :show-center-label? - Show total in center (default: true)
   - :show-legend? - Show legend (default: true)
   - :show-labels? - Show labels on slices (default: true)"
  [_]
  (let [click-timer* (atom nil)
        hovered-slice- (re/ratom nil)
        hovered-item- (re/ratom nil)
        mouse-position* (re/ratom {:x 0 :y 0})
        chart-bounds* (re/ratom nil)]
    (fn [{:keys [data width height inner-radius outer-radius show-center-label? show-legend? show-labels? cell-config
                 tooltip? tooltip-config name-key pie-config label-fn data-key data-keys-config]
          :or {width 500
               height 500
               inner-radius 50
               outer-radius 80
               show-center-label? true
               show-legend? true
               show-labels? true
               tooltip? true
               name-key :name
               data-key "value"}}]
      (let [slice-fill (fn [item]
                         (let [slice (str (get item name-key))
                               cfg (or (get data-keys-config slice)
                                       (get data-keys-config (keyword slice)))]
                           (or (some #(get cfg %) [:fill "fill" :stroke "stroke"])
                               (:fill item)
                               (recharts.util/color-from-string slice))))
            data (->> data
                      (sort-by #(or (get % data-key)
                                    (get % (keyword data-key))))
                      (reverse)
                      (mapv #(assoc % :fill (slice-fill %))))
            total (recharts.util/calculate-total data :value-key data-key)
            legend-data (recharts.util/prepare-legend-data {:data data
                                                            :name-key name-key
                                                            :value-key data-key
                                                            :total total})
            current-hover @hovered-slice-
            has-hover (some? current-hover)
            calc-opacity (fn [item]
                           (if has-hover
                             (if (= current-hover (get item name-key))
                               1.0
                               0.33)
                             1.0))
            data-with-opacity (mapv #(assoc % :opacity (calc-opacity %)) data)
            max-size (min width height)]
        [legend/chart-with-legend
         {:legend-data legend-data
          :show-legend? show-legend?
          :max-size max-size}
         (fn [size]
           [:div {:style {:width (str size "px")
                          :height (str size "px")
                          :max-width "100%"
                          :aspect-ratio 1
                          :font-family style/roboto-font-family
                          :position :relative
                          :flex-shrink 0}
                  :on-mouse-move (fn [e]
                                   (let [target (.-currentTarget e)
                                         bounds (.getBoundingClientRect target)]
                                     (reset! chart-bounds* bounds)
                                     (reset! mouse-position* {:x (.-clientX e)
                                                              :y (.-clientY e)})))}
            [recharts.util/re-responsive-container {:width "100%" :height size}
             [recharts.util/re-pie-chart {}
              (into [recharts.util/re-pie (merge {:data (clj->js data-with-opacity)
                                                  :dataKey data-key
                                                  :cx "50%"
                                                  :cy "50%"
                                                  :innerRadius (str inner-radius "%")
                                                  :outerRadius (str outer-radius "%")
                                                  :label (when show-labels? #(label/text-label % {:label-fn label-fn
                                                                                                  :name-key name-key}))
                                                  :labelLine false
                                                  :isAnimationActive false}
                                                 pie-config)]
                    (mapv (fn [item]
                            (let [on-click (or (:on-click item) (:on-click cell-config))
                                  on-double-click (or (:on-double-click item) (:on-double-click cell-config))]
                              [recharts.util/re-cell {:key (get item name-key)
                                                      :fill (:fill item)
                                                      :fillOpacity (:opacity item)
                                                      :onMouseEnter #(do
                                                                       (reset! hovered-slice- (get item name-key))
                                                                       (reset! hovered-item- item))
                                                      :onMouseLeave #(do
                                                                       (reset! hovered-slice- nil)
                                                                       (reset! hovered-item- nil))
                                                      :onClick (if (and on-click on-double-click)
                                                                 #(do
                                                                    (when @click-timer* (js/clearTimeout @click-timer*))
                                                                    (reset! click-timer* (js/setTimeout (fn [] (on-click % item))
                                                                                                        250)))
                                                                 on-click)
                                                      :stroke (style/alc-color "Background/card_hex")
                                                      :onDoubleClick #(when on-double-click
                                                                        (when @click-timer*
                                                                          (js/clearTimeout @click-timer*)
                                                                          (reset! click-timer* nil))
                                                                        (on-double-click % item)
                                                                        (util/stop-event %))
                                                      :style {:outline "none"}}]))
                          data-with-opacity))]]
            (when show-center-label?
              [label/center-label {:total total
                                   :hovered-item-data @hovered-item-
                                   :name-key name-key
                                   :value-key data-key
                                   :scale (/ size max-size)
                                   :hole-size (* size (/ inner-radius 100))}])
            (when tooltip?
              [custom-stack-tooltip (merge {:hovered-item* hovered-item-
                                            :mouse-position* mouse-position*
                                            :chart-bounds* chart-bounds*}
                                           tooltip-config)])])]))))